import { Box } from "@mui/material";
import { DeleteOutlineOutlined as DeleteIcon } from "@mui/icons-material";
import { useState } from "react";
import { v2 } from "../ui/v2Tokens";
import V2Dialog from "../ui/V2Dialog";
import { Field, NumberInput, Pill, PrimaryButton, TextButton, TextInput } from "../ui/primitives";
import {
  companyFoodToItem,
  createCustomItem,
  type CompanyFoodDraft,
  type FoodItemV2,
} from "../model/nutritionV2Model";
import { createCompanyFood } from "../../../../services/NutritionV2Service";
import { GRAM, KCAL } from "../ui/format";
import {
  POINTS,
  POINTS_SHORT,
  energyFromInput,
  energyLabel,
  macroFromInput,
  type NutritionDisplayMode,
} from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";

type Mode = "free" | "company";

type Props = {
  open: boolean;
  onClose: () => void;
  onAdd: (item: FoodItemV2) => void;
};

type ServingForm = {
  servingName: string;
  grams: string;
  calories: string;
  protein: string;
  carbs: string;
  fats: string;
  fiber: string;
};

const emptyServing = (servingName = "100 גרם", grams = "100"): ServingForm => ({
  servingName,
  grams,
  calories: "",
  protein: "",
  carbs: "",
  fats: "",
  fiber: "",
});

const MACRO_FIELDS: Array<{ key: keyof ServingForm; label: string; suffix: string; step: number }> = [
  { key: "calories", label: "קלוריות", suffix: KCAL, step: 1 },
  { key: "protein", label: "חלבון", suffix: GRAM, step: 0.1 },
  { key: "carbs", label: "פחמימות", suffix: GRAM, step: 0.1 },
  { key: "fats", label: "שומן", suffix: GRAM, step: 0.1 },
  { key: "fiber", label: "סיבים", suffix: GRAM, step: 0.1 },
];

/**
 * The same fields typed in points: the energy field is named and measured in נקודות, each macro in
 * its own points. Fibre has no points value and stays in grams.
 */
const POINTS_FIELDS: typeof MACRO_FIELDS = MACRO_FIELDS.map((field) =>
  field.key === "fiber"
    ? field
    : {
        ...field,
        label: field.key === "calories" ? energyLabel(POINTS) : field.label,
        suffix: POINTS_SHORT,
        step: 0.1,
      }
);

/**
 * A serving as typed → kcal and grams. The form holds exactly what the trainer typed (so "1." is
 * never eaten mid-keystroke); the unit is converted once, here, on the way out.
 */
const servingNutrition = (s: ServingForm, mode: NutritionDisplayMode) => ({
  calories: energyFromInput(Number(s.calories) || 0, mode),
  protein: macroFromInput("protein", Number(s.protein) || 0, mode),
  carbs: macroFromInput("carbs", Number(s.carbs) || 0, mode),
  fats: macroFromInput("fats", Number(s.fats) || 0, mode),
  fiber: Number(s.fiber) || 0,
});

const note = {
  fontSize: "11.5px",
  color: v2.ink2,
  lineHeight: 1.6,
  padding: "9px 12px",
  borderRadius: v2.rSm,
  background: v2.canvas,
  border: `1px solid ${v2.line}`,
};

/**
 * Adds something the system food database doesn't have. Two very different things:
 *  - "קלוריות חופשיות" — a calorie allowance the trainee spends as they like. Not a
 *    food; it lives only in the menu that granted it.
 *  - "מזון חדש" — a real food, saved to the COMPANY's food list and from then on
 *    searchable in every menu, exactly like a system food.
 */
const CustomFoodDialog = ({ open, onClose, onAdd }: Props) => {
  const [mode, setMode] = useState<Mode>("free");
  const units = useNutritionDisplayMode();
  const inPoints = units === POINTS;
  // "קלוריות חופשיות" names the allowance by its metric, so a points company grants נקודות חופשיות
  const freeName = `${energyLabel(units)} חופשיות`;
  const fields = inPoints ? POINTS_FIELDS : MACRO_FIELDS;
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [barcode, setBarcode] = useState("");
  const [calories, setCalories] = useState("");
  const [servings, setServings] = useState<ServingForm[]>([emptyServing()]);

  const reset = () => {
    setMode("free");
    setName("");
    setBrand("");
    setCalories("");
    setServings([emptyServing()]);
  };

  const close = () => {
    reset();
    onClose();
  };

  const patchServing = (index: number, changes: Partial<ServingForm>) =>
    setServings((list) => list.map((s, i) => (i === index ? { ...s, ...changes } : s)));

  const freeCalories = energyFromInput(Number(calories) || 0, units);
  const firstServingCalories = Number(servings[0]?.calories) || 0;
  const canSubmit =
    mode === "free" ? freeCalories > 0 : name.trim().length > 0 && firstServingCalories > 0;

  const submitFree = () =>
    onAdd(createCustomItem({ name: name.trim() || freeName, calories: freeCalories }));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submitCompanyFood = async () => {
    const draft: CompanyFoodDraft = {
      name,
      brand,
      barcode,
      // a serving with no calories was left blank by the trainer — drop it rather than
      // saving a zero serving that would silently zero out the meal when picked
      servings: servings
        .filter((s, i) => i === 0 || Number(s.calories) > 0)
        .map((s) => ({
          servingName: s.servingName,
          gramsPerServing: Number(s.grams) || undefined,
          nutrition: servingNutrition(s, units),
        })),
    };
    /*
     * The food is created on the SERVER first, and only the row that comes back is added to the
     * meal. Adding it optimistically would put a food in the menu that the company's food list has
     * never heard of — searchable nowhere, and gone the moment the menu is reloaded.
     */
    setSaving(true);
    setError("");
    try {
      const created = await createCompanyFood(draft);
      onAdd(companyFoodToItem(created));
      reset();
    } catch (e) {
      setError("לא הצלחנו לשמור את המזון. נסה שוב.");
    } finally {
      setSaving(false);
    }
  };

  const submit = () => {
    if (mode === "free") {
      submitFree();
      reset();
      return;
    }
    // the company-food path resets itself, but only once the server has confirmed
    void submitCompanyFood();
  };

  return (
    <V2Dialog
      open={open}
      onClose={close}
      title="הוסף מזון שאינו במאגר"
      subtitle={
        mode === "free"
          ? `קצובת ${energyLabel(units)} שהמתאמן מנצל איך שירצה`
          : "מזון חדש שיישמר במאגר של החברה"
      }
      maxWidth={mode === "free" ? "480px" : "620px"}
      actions={
        <>
          {error && (
            <Box data-testid="custom-food-error" role="alert"
                 sx={{ marginInlineEnd: "auto", fontSize: "12.5px", color: v2.over, fontWeight: 600 }}>
              {error}
            </Box>
          )}
          <TextButton onClick={close}>ביטול</TextButton>
          <PrimaryButton onClick={submit} disabled={!canSubmit || saving}>
            {mode === "free" ? "הוסף לארוחה" : saving ? "שומר…" : "צור והוסף לארוחה"}
          </PrimaryButton>
        </>
      }
    >
      <Box sx={{ display: "flex", gap: "6px", marginBottom: "14px", flexWrap: "wrap" }}>
        <Pill active={mode === "free"} onClick={() => setMode("free")}>
          {freeName}
        </Pill>
        <Pill active={mode === "company"} onClick={() => setMode("company")}>
          מזון חדש
        </Pill>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: mode === "company" ? { xs: "1fr", md: "1fr 1fr" } : "1fr",
            gap: "12px 16px",
          }}
        >
          <Field label="שם" sx={{ gridColumn: mode === "company" ? { md: "1 / -1" } : undefined }}>
            <TextInput
              ariaLabel="שם"
              placeholder={mode === "free" ? freeName : "שם המוצר"}
              value={name}
              onChange={setName}
            />
          </Field>
          {mode === "company" && (
            <>
            <Field label={<>מותג <Box component="span" sx={{ color: v2.ink4, fontWeight: 400 }}>· רשות</Box></>}>
              <TextInput ariaLabel="מותג" value={brand} onChange={setBrand} />
            </Field>
            {/* the app scans barcodes; without one here a food the trainer typed in can
                never be found that way, only by name */}
            <Field
              label={<>ברקוד <Box component="span" sx={{ color: v2.ink4, fontWeight: 400 }}>· רשות</Box></>}
              sx={{ gridColumn: { md: "1 / -1" } }}
            >
              <TextInput
                ariaLabel="ברקוד"
                placeholder="לסריקה באפליקציה של המתאמן"
                value={barcode}
                onChange={setBarcode}
              />
            </Field>
            </>
          )}
        </Box>

        {mode === "free" ? (
          <>
            <Field label={energyLabel(units)}>
              <NumberInput
                ariaLabel={energyLabel(units)}
                value={calories}
                placeholder={inPoints ? "2" : "200"}
                min={0}
                step={inPoints ? 0.1 : 10}
                suffix={inPoints ? POINTS_SHORT : KCAL}
                onChange={setCalories}
              />
            </Field>
            <Box sx={note}>
              {inPoints ? "הנקודות" : "הקלוריות"} ייספרו ביעד היומי, אבל בלי פירוט מקרו — המתאמן בוחר בעצמו מה לאכול
              במסגרת הקצובה הזו. הקצובה שייכת לתפריט הזה בלבד ולא נשמרת במאגר.
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: v2.ink2 }}>גדלי מנה</Box>
                <TextButton
                  onClick={() => setServings((list) => [...list, emptyServing("מנה", "")])}
                  sx={{ height: { xs: "36px", md: "28px" }, padding: "0 10px", fontSize: "12px" }}
                >
                  + הוסף גודל מנה
                </TextButton>
              </Box>

              {servings.map((serving, i) => (
                <Box
                  key={i}
                  data-testid="serving-form"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    padding: "10px",
                    borderRadius: v2.rSm,
                    border: `1px solid ${v2.line}`,
                    background: v2.canvas,
                  }}
                >
                  {/* A fixed 120px next to a 30px trash left the serving NAME about 100px on a
                      phone. Wrapping lets it take the first line and the grams the second. */}
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px", flexWrap: { xs: "wrap", md: "nowrap" } }}>
                    <Box sx={{ flex: { xs: "1 1 140px", md: 1 }, minWidth: 0 }}>
                      <Field label={i === 0 ? "שם המנה (ברירת המחדל)" : "שם המנה"}>
                        <TextInput
                          ariaLabel={`שם המנה ${i + 1}`}
                          value={serving.servingName}
                          onChange={(v) => patchServing(i, { servingName: v })}
                        />
                      </Field>
                    </Box>
                    <Box sx={{ width: { xs: "104px", md: "120px" }, flex: "none" }}>
                      <Field label="גרם למנה">
                        <NumberInput
                          ariaLabel={`גרם למנה ${i + 1}`}
                          value={serving.grams}
                          min={0}
                          step={1}
                          suffix={GRAM}
                          onChange={(v) => patchServing(i, { grams: v })}
                        />
                      </Field>
                    </Box>
                    {servings.length > 1 && (
                      <Box
                        component="button"
                        type="button"
                        aria-label={`מחק גודל מנה ${i + 1}`}
                        onClick={() => setServings((list) => list.filter((_, j) => j !== i))}
                        sx={{
                          width: { xs: "40px", md: "auto" },
                          height: { xs: "40px", md: "38px" },
                          flex: "none",
                          border: "none",
                          background: "transparent",
                          color: v2.ink3,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": { color: v2.over },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, minmax(0, 1fr))" },
                      gap: "8px",
                    }}
                  >
                    {fields.map((field) => (
                      <Field key={field.key} label={field.label}>
                        <NumberInput
                          ariaLabel={`${field.label} ${i + 1}`}
                          value={serving[field.key]}
                          min={0}
                          step={field.step}
                          suffix={field.suffix}
                          onChange={(v) => patchServing(i, { [field.key]: v } as Partial<ServingForm>)}
                        />
                      </Field>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={note}>
              המזון יישמר במאגר המזונות של החברה ויהיה זמין בחיפוש בכל התפריטים, לכל
              המאמנים בחברה. הוא לא יופיע אצל חברות אחרות ואינו מסומן כמאושר על ידינו.
            </Box>
          </>
        )}
      </Box>
    </V2Dialog>
  );
};

export default CustomFoodDialog;
